<template>
  <el-dialog
    class="export-excel-popup"
    title="Export dữ liệu ra excel"
    :width="isContinue ? '1024px' : '500px'"
    :visible="visible"
    :show-close="!exporting"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="$emit('close')"
  >
    <el-form
      v-if="!isContinue"
      label-position="top"
    >
      <el-form-item label="Tiêu đề file">
        <el-input :value="title" />
      </el-form-item>
      <div class="w-full">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="`Từ dòng:`">
              <el-input-number
                v-model="from"
                :min="1"
                :max="to"
                :disabled="exporting"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="`Đến dòng:`">
              <el-input-number
                v-model="to"
                :min="from"
                :max="total"
                :disabled="exporting"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </div>
      <el-form-item label="Trường dữ liệu">
        <el-select
          v-model="selectedColumns"
          class="w-100"
          placeholder="Chọn các trường dữ liệu muốn export"
          filterable
          multiple
        >
          <el-option
            v-for="c in columns"
            :key="c.key"
            :label="c.name"
            :value="c.key"
          />
        </el-select>
      </el-form-item>
      <span>Nếu không chọn trường dữ liệu nào thì hệ thống sẽ export toàn bộ</span>
      <div
        v-if="exporting"
        class="mt-s"
      >
        <el-progress
          :percentage="percentage"
          :stroke-width="12"
        />
      </div>
    </el-form>
    <div v-else>
      <el-row
        class="mt-0 mb-xs"
        type="flex"
        justify="space-between"
        align="middle"
      >
        <h3 class="text-center mt-0">
          {{ excelTitle }} ({{ checkedExportData.length }})
        </h3>
        <el-input
          v-model="searchText"
          class="w-50"
          placeholder="Nhập thông tin để tìm kiếm"
          @input="() => (selectedRows = [])"
        />
      </el-row>
      <el-table
        ref="multipleTable"
        border
        stripe
        :data="filteredExportData.map((d, index) => ({ ...d, stt: index + 1 }))"
        style="width: 100%"
        height="450"
      >
        <el-table-column
          key="checkbox"
          label=""
          width="50"
          fixed="left"
        >
          <template slot-scope="scope">
            <el-checkbox
              :value="selectedRows.includes(scope.row.stt)"
              @change="(v) => handleSelectRow(scope.row.stt, v)"
            />
          </template>
        </el-table-column>
        <el-table-column
          prop="stt"
          label="STT"
          align="center"
          width="80"
        >
          <template slot-scope="scope">
            {{ scope.row.stt }}
          </template>
        </el-table-column>
        <el-table-column
          v-for="column in excelColumns"
          :key="column.key"
          :prop="column.key"
          :label="column.label"
          :min-width="column.width"
          :fixed="column.fixed"
        >
          <template slot-scope="scope">
            <span v-if="[fieldType.DATE, fieldType.DATETIME].includes(column.type)">
              {{ scope.row[column.key] | convertDateTime(column.format) }}
            </span>
            <span v-else-if="[fieldType.SELECT].includes(column.type) && column.options.type">
              {{ scope.row[column.key] | categoryName(allCategories) }}
            </span>
            <span
              v-else
              class="text-limited text-limited__2"
              :title="scope.row[column.key]"
            >
              {{
                column.locale
                  ? scope.row[column.locale][column.column_key]
                  : scope.row[column.key]
              }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <span slot="footer">
      <el-button
        v-if="!isContinue"
        type="primary"
        :loading="exporting"
        @click="getExportData"
      >
        Tiếp tục
      </el-button>
      <el-row
        v-else
        type="flex"
        align="middle"
        justify="space-between"
      >
        <el-button
          type="info"
          class="mr-s"
          :loading="exporting"
          @click="isContinue = false"
        >
          Quay lại
        </el-button>
        <div>
          <el-button
            type="primary"
            icon="el-icon-download"
            class="mr-s"
            :loading="exporting"
            :disabled="checkedExportData.length === 0"
            @click="handleExportJson"
          >
            Export JSON
          </el-button>
          <vue-excel-xlsx
            :columns="excelColumns"
            :data="excelData"
            :file-name="`${excelTitle}`"
            :sheet-name="`${excelTitle}`"
            style="border: none; padding: 0"
          >
            <el-button
              type="primary"
              icon="el-icon-download"
              :disabled="checkedExportData.length === 0"
              @click="handleExportExcel"
            >
              Download Excel
            </el-button>
          </vue-excel-xlsx>
        </div>
      </el-row>
    </span>
  </el-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import commonServices from "@/services/common";
import organizationServices from "@/services/organization"
import { convertData, JSONToFile } from "@/utils/export";
import { categoryName, convertDateTime } from "@/utils/filters";
import constants from "@/config/constants";

export default {
  name: "ExportExcelPopup",
  components: {},
  props: {
    title: String,
    visible: Boolean,
    total: Number,
    columns: Array,
    type: String,
    objectType: String,
    filterParams: Object,
  },
  data() {
    return {
      exporting: false,
      from: 1,
      to: this.total,
      selectedColumns: [],
      exportData: [],
      isContinue: false,
      importing: false,
      exported: 0,
      exportSize: 100,
      selectedRows: [],
      searchText: "",
      fieldType: constants.FIELD_TYPE,
      organizationId: organizationServices.getCurrentOrg()?.id
    };
  },
  computed: {
    ...mapGetters("category", ["allCategories"]),
    excelTitle() {
      return this.title;
    },
    limit() {
      return this.to - this.from + 1;
    },
    startPage() {
      return Math.floor(this.from / this.exportSize) + 1;
    },
    endPage() {
      return Math.floor(this.to / this.exportSize) + 1;
    },
    percentage() {
      if (this.limit > 0) {
        const result = Math.ceil((this.exported / this.limit) * 100);
        return result > 100 ? 100 : Number(result);
      }
      return 0;
    },
    requestType() {
      if (this.objectType === "categories") {
        return "extras";
      }
      return this.objectType;
    },
    requestParams() {
      if (this.objectType === "categories") {
        return {
          locale: "vi",
          filters: {
            organization_id: this.organizationId,
            type: this.type,
          }
        }
      }
      return this.filterParams
    },
    excelColumns() {
      return this.columns.filter((c) => {
        if (this.selectedColumns.length > 0) {
          return this.selectedColumns.includes(c.key)
        }
        return true
      }).map((c) => ({
        ...c,
        label: c.label || c.name,
        field: c.key,
        width: c.width || 150,
      }))
    },
    filteredExportData() {
      return this.exportData.filter((d) => {
        if (this.searchText) {
          let result = false;
          const keys = Object.keys(d);
          for (let i = 0; i < keys.length; i++) {
            if (d[keys[i]]) {
              if (typeof d[keys[i]] !== "object") {
                const value = d[keys[i]].toString();
                if (
                  value.toLowerCase().includes(this.searchText.toLowerCase())
                ) {
                  result = true;
                }
              } else {
                const oKeys = Object.keys(d[keys[i]]);
                for (let j = 0; j < oKeys.length; j++) {
                  if (d[keys[i]][oKeys[j]]) {
                    if (typeof d[keys[i]][oKeys[j]] !== "object") {
                      const value = d[keys[i]][oKeys[j]].toString();
                      if (
                        value
                          .toLowerCase()
                          .includes(this.searchText.toLowerCase())
                      ) {
                        result = true;
                      }
                    }
                  }
                }
              }
            }
            if (result) {
              break;
            }
          }
          return result;
        }
        return true;
      });
    },
    checkedExportData() {
      if (this.selectedRows.length > 0) {
        return this.filteredExportData.filter((d, index) =>
          this.selectedRows.includes(index + 1)
        );
      } else {
        return this.filteredExportData;
      }
    },
    excelData() {
      return this.checkedExportData.map((d) => {
        const data = {
          id: d.id || ""
        };
        this.excelColumns.forEach((column) => {
          if (column.locale) {
            data[`${column.key}`] = d[column.locale][column.column_key];
          } else if ([this.fieldType.DATE, this.fieldType.DATETIME].includes(column.type)) {
            data[`${column.key}`] = convertDateTime(d[column.key], column.format);
          } else if ([this.fieldType.SELECT, this.fieldType.MULTIPLE_SELECT].includes(column.type) && column.options.type) {
            data[`${column.key}`] = categoryName(d[column.key], this.allCategories);
          } else {
            data[`${column.key}`] = d[column.key];
          }
        });
        return data;
      });
    },
  },
  methods: {
    handleSelectRow(stt, isChecked) {
      if (isChecked) {
        this.selectedRows = [...this.selectedRows, stt];
      } else {
        this.selectedRows = this.selectedRows.filter((r) => r !== stt);
      }
    },

    async handleListData(params) {
      return await commonServices
        .list(this.requestType, params)
        .then(async (response) => {
          return response.items.map((item) =>
            convertData({ id: item.id, ...item.extra }, this.columns)
          );
        })
        .catch(() => {
          return {
            result: [],
          };
        });
    },

    async getExportData() {
      this.exportData = [];
      this.selectedRows = [];
      let exportData = [];
      this.exporting = true;
      let page = this.startPage;
      let params = this.requestParams;
      while (page <= this.endPage) {
        params = {
          ...params,
          offset: (page - 1) * this.exportSize,
          limit: page * this.exportSize,
        };
        const result = await this.handleListData(params);
        exportData = [...exportData, ...result];
        this.exported = exportData.length;
        page = page + 1;
      }
      const offset = this.from - this.exportSize * (this.startPage - 1) - 1;
      this.exportData = exportData.slice(
        offset > 0 ? offset : 0,
        this.limit + offset
      );
      setTimeout(() => {
        this.isContinue = true;
        this.exporting = false;
      }, 1000);
    },

    handleExportJson() {
      JSONToFile(this.excelData, this.excelTitle);
      this.$emit("close");
      this.$notify.success({
        title: "Thông báo !",
        message: "Lưu file JSON thành công !",
      });
    },
    handleExportExcel() {
      this.$emit("close");
      this.$notify.success({
        title: "Thông báo !",
        message: "Lưu file Excel thành công !",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.export-excel-popup {
  .el-select,
  .el-input-number {
    width: 100%;
  }
}
</style>
<style lang="scss">
.export-excel-popup {
  .el-dialog__body {
    padding-top: 10px;
    .el-table {
      .el-table__header-wrapper {
        table {
          thead {
            tr {
              th {
                .cell {
                  word-break: break-word !important;
                }
              }
            }
          }
        }
      }
    }
    .el-form-item__label {
      padding-bottom: 0 !important;
    }
  }
}
</style>
